import { useReducer } from 'react'
import { Modal } from '../../../components/Modal/Modal'
import { BulkInviteResponse, CheckedCsvInviteUser, CsvInviteUser, useBulkInviteMutation } from '../../../store/auth/auth'
import { selectAccountId } from '../../../store/auth/authSlice'
import { useAppDispatch, useAppSelector } from '../../../store/hooks'
import { planApi, useGetBillingCustomerFreeSeatsQuery } from '../../../store/plan/plan'
import { BulkInvitationModalBody } from './BulkInvitationModalBody'
import { InvitationInProgress } from './InvitationInProgress'
import { InvitationUserList } from './InvitationUserList'
import { InvitePreparationSummary } from './InvitePreparationSummary'
import {
  bulkInvitationContextReducer,
  csvClientVerificationCompleted,
  csvClientVerificationStarted,
  csvServerVerificationCompleted,
  csvServerVerificationStarted,
  invitationCompleted,
  invitationResultChanged,
  processedInvitationCountChanged,
  reset,
  startInvitationLoading,
} from './state/reducer'
import { initialBulkInviteContextState } from './state/state'
import { hasClientOrServerVerificationError, hasInvitationError, isCsvVerificationCompletedWithNoError } from './utils'
import { VerificationErrorAlert } from './VerificationErrorAlert'
import { roleGroups } from '../InviteRoleGroups'

// Keep invite batches intentionally small to reduce request size and avoid backend processing/time-out issues during bulk invites.
const _INVITE_CHUNK_SIZE = 5

export interface BulkInvitationModalProps {
  onHide: () => void
}

export const BulkInvitationModal = ({ onHide }: BulkInvitationModalProps) => {
  const [state, dispatch] = useReducer(bulkInvitationContextReducer, initialBulkInviteContextState)
  const accountId = useAppSelector(selectAccountId)
  const [bulkInviteApiCall, { isLoading }] = useBulkInviteMutation()
  const { data: freeSeatsData, isFetching: isFreeSeatsFetching, isError: isFreeSeatsFetchingError } = useGetBillingCustomerFreeSeatsQuery({ accountId })
  const appDispatch = useAppDispatch()

  const handleBulkInvite = async () => {
    if (!state.users.length) {
      return
    }
    try {
      dispatch(startInvitationLoading())

      let chunk = []
      let processedCount = 0
      const inviteResult: BulkInviteResponse = { users: [], generalErrors: [] }
      for (const { index, user } of state.users.map((user, index) => ({ index, user }))) {
        chunk.push(user)
        if (chunk.length === _INVITE_CHUNK_SIZE || index === state.users.length - 1) {
          const partialResult = await bulkInviteApiCall({
            accountId,
            users: chunk.map((checkedUser) => ({
              ...checkedUser,
              roleIds: roleGroups.find((roleGroup) => roleGroup.name === checkedUser.role)?.roleIds ?? [],
              errors: undefined,
              warnings: undefined,
              inviteStatus: undefined,
            })),
          }).unwrap()
          processedCount += partialResult.users.length
          inviteResult.users.push(...partialResult.users)
          inviteResult.generalErrors.push(...partialResult.generalErrors)
          dispatch(processedInvitationCountChanged(processedCount))
          chunk = []
        }
      }
      appDispatch(planApi.util.invalidateTags(['BillingCustomer']))
      dispatch(invitationResultChanged(inviteResult.users))
      if (inviteResult.users.every((user) => !user.isError) && inviteResult.generalErrors.length === 0) {
        onHide()
      }
    } catch {
      dispatch(invitationResultChanged([]))
    } finally {
      dispatch(invitationCompleted())
    }
  }

  const handleFileSelected = () => {
    dispatch(reset())
    dispatch(csvClientVerificationStarted())
  }

  const handleCsvClientVerificationCompleted = (users: CsvInviteUser[], error: Error | null) => {
    dispatch(csvClientVerificationCompleted(users, error ? (error.message ?? 'Unexpected error occurred, please try again') : null))
  }

  const handleCsvServerVerificationStarted = () => {
    dispatch(csvServerVerificationStarted())
  }

  const handleCsvServerVerificationCompleted = (users: CheckedCsvInviteUser[], errors: string[]) => {
    dispatch(csvServerVerificationCompleted(users, errors))
  }

  const isVerificationError = hasClientOrServerVerificationError(state)
  const isInvitationError = hasInvitationError(state)
  const isValidCsv = isCsvVerificationCompletedWithNoError(state)

  const hasNoAvailableSeat = freeSeatsData ? freeSeatsData.data.seats.general.available === 0 && freeSeatsData.data.seats.sidecar.available === 0 : true

  return (
    <Modal
      show
      onHide={state.invite.isInvitationLoading ? undefined : onHide}
      size='xl'
      headerBodySeparator={true}
      header={<h4 className='d-flex'>Bulk Invitation</h4>}
      body={
        state.invite.isInvitationLoading ? (
          <InvitationInProgress
            count={state.users?.filter((user) => 'inviteStatus' in user && user.inviteStatus === null).length ?? 0}
            processed={state.invite.processedInvitationCount}
          />
        ) : (
          <BulkInvitationModalBody
            accountId={accountId}
            onFileSelected={handleFileSelected}
            onCsvClientVerificationCompleted={handleCsvClientVerificationCompleted}
            onCsvServerVerificationStarted={handleCsvServerVerificationStarted}
            onCsvServerVerificationCompleted={handleCsvServerVerificationCompleted}
            freeSeatsData={freeSeatsData?.data}
            isFreeSeatsFetching={isFreeSeatsFetching}
            isFreeSeatsFetchingError={isFreeSeatsFetchingError}
            error={
              <VerificationErrorAlert
                clientSideVerificationError={state.csv.clientSideVerificationError}
                serverSideVerificationErrors={state.csv.serverSideVerificationErrors}
                hasClientOrServerVerificationError={isVerificationError}
                usersCount={state.users.length}
              />
            }
          >
            {state.csv.serverSideVerificationLoading || isVerificationError || isInvitationError ? (
              <InvitationUserList
                users={state.users}
                isInvitationLoading={state.invite.isInvitationLoading}
                invitationResult={state.invite.results}
                displayErrorRecordsOnly={!state.csv.serverSideVerificationLoading}
              />
            ) : null}
            {isValidCsv && !isInvitationError ? <InvitePreparationSummary users={state.users as CheckedCsvInviteUser[]} /> : null}
          </BulkInvitationModalBody>
        )
      }
      showFooterActions={true}
      onFooterPrimaryAction={handleBulkInvite}
      footerPrimaryActionLoading={isLoading}
      footerPrimaryActionDisabled={!isValidCsv || isFreeSeatsFetching || isFreeSeatsFetchingError || hasNoAvailableSeat}
      footerPrimaryActionButtonVariant='primary'
      footerPrimaryActionTitle='Invite Users'
      footerSecondaryActionDisabled={state.invite.isInvitationLoading}
      backdrop={state.invite.isInvitationLoading ? 'static' : undefined}
      keyboard={!state.invite.isInvitationLoading}
    />
  )
}
